/**
 * The small pieces For the crew and From the crew draw again and again
 * (Boards 9, 10, 16), each at the board's own numbers: the source chip, the
 * pill chips, a section's head with its count, the 22px state chip with its
 * dot, the green link, and a box that measures its own width (§19.1: "cards
 * measured by the panel").
 */
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import type { CrewNoteSource, DayChip } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { StatusChip } from '../components/primitives.js'
import { VisitGlyph, type VisitGlyphName } from './glyphs.js'
import { COMMON, FOR_CREW } from './copy.js'

/** How wide this box is drawn — the tab's own panel, never the window. */
export function useBoxWidth<T extends HTMLElement = HTMLDivElement>(): [RefObject<T>, number] {
  const box = useRef<T>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const element = box.current
    if (!element) return undefined
    const measure = () => setWidth(element.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const watcher = new ResizeObserver(measure)
    watcher.observe(element)
    return () => watcher.disconnect()
  }, [])
  return [box, width]
}

/**
 * Where a note, a file or a layer lives — 19px, 4px radius, 11/600 (Board 9):
 * *This visit* teal · *Property* blue · *Contact* green · *Job* amber.
 */
const SOURCE_TONES: Record<CrewNoteSource, string> = {
  visit: 'bg-teal-band-bg text-teal-band-text',
  property: 'bg-info-bg text-info',
  contact: 'bg-accent-soft text-accent-dark',
  job: 'bg-amber-band-bg text-amber-band-text',
}

export function SourceChip({ source, className }: { source: CrewNoteSource; className?: string }) {
  return (
    <span
      className={cn('inline-flex h-[19px] shrink-0 items-center gap-1 whitespace-nowrap rounded-tag px-1.5',
        'text-[11px] font-semibold', SOURCE_TONES[source], className)}
    >
      {FOR_CREW.source[source]}
    </span>
  )
}

/**
 * A pill chip — green when selected (do-not-substitute 14), never black.
 * `filter`: the tab's 30px chips with the tick (Boards 9, 10); `pick`: the
 * composer's and the lightbox's 28px chips with the tick; `phone`: Board 16's
 * 30px chips, no tick, with a 44px tap area around the drawing.
 */
export function PillChip({
  selected, onClick, children, look = 'filter', disabled, className,
}: {
  selected: boolean
  onClick?: () => void
  children: ReactNode
  look?: 'filter' | 'pick' | 'phone'
  disabled?: boolean
  className?: string
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-chip border font-semibold',
        look === 'filter' && 'h-[30px] px-3 text-[12.5px]',
        look === 'pick' && 'h-7 px-2.5 text-[12px]',
        look === 'phone' && "h-[30px] px-[11px] text-[12.5px] after:absolute after:-inset-y-[7px] after:inset-x-0 after:content-['']",
        selected ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text',
        !selected && !disabled && 'hover:bg-hover',
        disabled && 'cursor-default',
        className,
      )}
    >
      {selected && look !== 'phone' && <VisitGlyph name="check" size={12} stroke={2.6} />}
      {children}
    </button>
  )
}

/** The grey count after a section's title — *Notes* `4` (11/600, a pill on `line-soft`). */
export function CountPill({ n }: { n: number }) {
  return (
    <span className="rounded-chip bg-line-soft px-[7px] py-px text-[11px] font-semibold text-text-muted">{n}</span>
  )
}

/** A section's head — *Notes* `4` … *newest first · pinned on top* (Board 9). */
export function SectionHead({ title, count, right }: { title: string; count?: number; right?: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <b className="text-[14.5px] font-bold text-text">{title}</b>
      {count !== undefined && <CountPill n={count} />}
      <span className="flex-1" />
      {right}
    </div>
  )
}

/** A day's chip (§7.4), as the From the crew day cards draw it — `StatusChip`'s `visitDay` pairs. */
export function DayStateChip({ chip }: { chip: DayChip }) {
  const words = chip.kind === 'closed_out' ? COMMON.closedOut
    : chip.kind === 'on_site' || chip.kind === 'on_the_clock' ? COMMON.onSite
      : chip.kind === 'prep' ? COMMON.prep(String(chip.ready), String(chip.total))
        : chip.kind === 'ready' ? COMMON.ready
          : chip.kind === 'scheduled' ? COMMON.scheduled : COMMON.skipped
  return <StatusChip group="visitDay" status={chip.kind}>{words}</StatusChip>
}

/** The green link with its glyph — *✎ Edit*, *› Edit on the property*, *＋ Add a file for the crew* (12.5/600). */
export function GreenLink({
  glyph, children, onClick, href,
}: {
  glyph: VisitGlyphName
  children: ReactNode
  onClick?: () => void
  href?: string
}) {
  const className = cn('inline-flex items-center gap-1 whitespace-nowrap text-[12.5px] font-semibold text-accent',
    'hover:text-accent-dark')
  const inner = <><VisitGlyph name={glyph} size={13} stroke={2.2} />{children}</>
  if (href) return <a href={href} className={className}>{inner}</a>
  return <button type="button" onClick={onClick} className={className}>{inner}</button>
}
